import { IconButton } from '@creditornot/cb-button';
import { MoreHorizontal as MoreHorizontalIcon } from '@creditornot/cb-icons';
import { colors, cssThemedValue, Typography as T } from '@creditornot/cb-ingredients';
import { MenuActionItem } from '@creditornot/cb-menu';
import { Popover } from '@creditornot/cb-popover';
import React, { FC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList } from 'react-window';
import styled from 'styled-components';

import { localize } from 'common/utils';

import VenueNameColumn from './VenueNameColumn';
import VenueStatusColumn from './VenueStatusColumn';
import { VenueRow } from '../types';

type Props = {
  venues: VenueRow[];
  handleRemoveAction?: (venue: VenueRow) => void;
};

const ActionsContainer = styled.div`
  min-width: 120px;
  padding: 8px;
  border-radius: 8px;
  overflow: hidden;
`;

const ActionsMenuItem = styled(MenuActionItem)`
  margin-block-start: 8px;
  margin-block-end: 8px;
`;

export const TableRow = styled.div`
  align-items: center;
  border-block-end: 1px solid ${cssThemedValue('borderPrimary')};
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  width: 100%;
  padding-inline: 24px;
  color: ${cssThemedValue('textPrimary')};
  cursor: pointer;
  &:hover {
    background-color: ${cssThemedValue({ light: colors.wolt8, dark: colors.salt12 })};
  }
`;

const ContactColumn = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Root = styled(AutoSizer)`
  height: 100%;
`;

type ActionsMenuProps = {
  venueRow: VenueRow;
  handleRemoveAction: (venue: VenueRow) => void;
};

const ActionsMenu: FC<ActionsMenuProps> = ({ handleRemoveAction, venueRow }) => (
  <div data-testid="actions-popover">
    <Popover
      placement="bottom-end"
      showOnClick
      showOnHover={false}
      renderArrow={false}
      closeOnClickInside
      disableContentPadding
      style={{ display: 'block', lineHeight: 0, maxWidth: 20, margin: 'auto' }}
      content={
        <ActionsContainer>
          <ActionsMenuItem
            variant="red"
            onClick={e => {
              e.stopPropagation();
              handleRemoveAction(venueRow);
            }}
          >
            <FormattedMessage id="state.remove" />
          </ActionsMenuItem>
        </ActionsContainer>
      }
    >
      <IconButton variant="lightGrey" size="small" background="animated">
        <MoreHorizontalIcon />
      </IconButton>
    </Popover>
  </div>
);

const ROW_HEIGHT = 56;

const VenueAvailabilityStatusTable: FC<Props> = ({ venues, handleRemoveAction }) => {
  const intl = useIntl();
  const navigateToVenue = (venueId: string) => window.open(`/venue/${venueId}`, '_blank');

  return (
    <Root>
      {({ height, width }) => (
        <FixedSizeList height={height} width={width} itemCount={venues.length} itemSize={ROW_HEIGHT} itemData={venues}>
          {({ index, style }) => {
            const item = venues[index];
            return (
              <TableRow onClick={() => navigateToVenue(item.id)} style={style} data-testid="table-row">
                <VenueNameColumn name={item.name} address={item.address} listImage={item.listImage} />
                <VenueStatusColumn status={item.status} />
                <T.Body3>{localize(item.merchantName, intl.locale)}</T.Body3>
                <ContactColumn>
                  <T.Body3>{item.contact || '-'}</T.Body3>
                  {handleRemoveAction && <ActionsMenu handleRemoveAction={handleRemoveAction} venueRow={item} />}
                </ContactColumn>
              </TableRow>
            );
          }}
        </FixedSizeList>
      )}
    </Root>
  );
};

export default VenueAvailabilityStatusTable;
